/**
 * AD-LITE UI COMPONENTS
 * 
 * Brand-safe ad placeholders and interstitial modals for web preview.
 * Native apps use AdMob directly.
 * 
 * PLACEMENT RULES:
 * - Never overlap navigation buttons
 * - Never overlap Ustadh chat interface
 * - Never interrupt active reading/prayer
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Crown, X, Sparkles, Clock } from 'lucide-react';
import { usePro } from '../contexts/ProContext';
import { adLiteService, AD_LITE_CONFIG } from '../services/adService';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';

const isNativeApp = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

/**
 * Interstitial Ad Modal (Web Placeholder)
 * Used for Launch-Gate, Task-Completion, and Exit-Transition ads
 */
export const InterstitialAdModal = ({ 
  isOpen, 
  onClose, 
  adType = 'general',
  fromModule = null 
}) => {
  const { promptUpgrade } = usePro();
  const { t } = useTranslation();
  const [countdown, setCountdown] = useState(5);
  
  useEffect(() => {
    if (!isOpen) {
      setCountdown(5);
      return;
    }
    
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, countdown]);
  
  if (!isOpen) return null;
  
  // Ad content based on type
  const getAdContent = () => {
    switch (adType) {
      case 'launch_gate':
        return {
          title: 'Welcome Back!',
          subtitle: 'Your Islamic companion awaits',
          icon: '🌙',
          gradient: 'from-emerald-600 to-teal-700',
        };
      case 'task_completion':
        return {
          title: 'MashAllah!',
          subtitle: 'Great progress on your journey',
          icon: '✨',
          gradient: 'from-amber-500 to-orange-600',
        };
      case 'exit_transition':
        return {
          title: 'Before You Go...',
          subtitle: 'A message from our sponsor',
          icon: '📿',
          gradient: 'from-indigo-600 to-purple-700',
        };
      default:
        return {
          title: 'Sponsored Message',
          subtitle: 'Support Tanzeen+ development',
          icon: '🕌',
          gradient: 'from-emerald-600 to-teal-700',
        };
    }
  };
  
  const content = getAdContent();
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      data-testid="interstitial-ad-modal"
    >
      <div className={`relative w-full max-w-sm bg-gradient-to-br ${content.gradient} rounded-2xl overflow-hidden shadow-2xl`}>
        {/* Close Button */}
        <button
          onClick={countdown === 0 ? onClose : undefined}
          disabled={countdown > 0}
          className={`absolute top-3 right-3 p-2 rounded-full transition-all z-10 ${
            countdown === 0 
              ? 'bg-white/20 hover:bg-white/30 text-white' 
              : 'bg-white/10 text-white/40 cursor-not-allowed'
          }`}
          data-testid="ad-close-btn"
        >
          {countdown > 0 ? (
            <span className="text-sm font-bold w-5 h-5 flex items-center justify-center">{countdown}</span>
          ) : (
            <X className="h-5 w-5" />
          )}
        </button>
        
        {/* Ad Content */}
        <div className="p-8 text-center text-white">
          <div className="text-5xl mb-4">{content.icon}</div>
          
          <h3 className="text-xl font-bold mb-2">{content.title}</h3>
          <p className="text-white/80 text-sm mb-6">{content.subtitle}</p>
          
          {/* Islamic-Friendly Ad Placeholder */}
          <div className="bg-white/10 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center text-2xl">📚</div>
              <div className="text-left flex-1">
                <p className="font-semibold text-sm">{t('adBanner.academy', "Islamic Learning Academy")}</p>
                <p className="text-xs text-white/70">{t('adBanner.academySub', "Learn Arabic & Quran online")}</p>
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                promptUpgrade();
                onClose();
              }}
              className="w-full py-3 bg-white text-emerald-700 font-semibold rounded-xl hover:bg-white/90 transition-colors"
              data-testid="upgrade-to-remove-ads"
            >
              <Crown className="h-4 w-4 inline mr-2" />
              Remove Ads Forever
            </button>
            
            <button
              onClick={countdown === 0 ? onClose : undefined}
              disabled={countdown > 0}
              className={`w-full py-2 text-sm transition-colors ${
                countdown === 0 
                  ? 'text-white/80 hover:text-white' 
                  : 'text-white/40 cursor-not-allowed'
              }`}
            >
              {countdown > 0 ? `Continue in ${countdown}s...` : 'Continue to App'}
            </button>
          </div>
        </div>
        
        {/* Ad Label */}
        <div className="absolute bottom-2 left-2 text-[8px] text-white/40 bg-white/10 px-1.5 py-0.5 rounded">
          SPONSORED • G-RATED
        </div>
        
        {/* Session Counter */}
        <div className="absolute bottom-2 right-2 text-[8px] text-white/40">
          Ad {adLiteService.sessionAdCount + 1}/{AD_LITE_CONFIG.MAX_ADS_PER_SESSION}
        </div>
      </div>
    </div>
  );
};

/**
 * Ad Manager Component
 * Listens to adLiteService events and shows appropriate ads
 * IMPORTANT: Ads should NEVER block the UI on first app load
 */
export const AdManager = () => {
  const { isPro } = usePro();
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [adType, setAdType] = useState('general');
  const [fromModule, setFromModule] = useState(null);
  
  useEffect(() => {
    // Don't show ads for Pro users
    if (isPro) return;
    
    // Initialize ad service (tracks launches)
    adLiteService.initialize();
    
    // Listen for ad events (web only)
    // These are triggered by specific user actions, not on page load
    const unsubscribe = adLiteService.addListener((event, data) => {
      if (isNativeApp()) return; // Native uses AdMob directly
      
      switch (event) {
        case 'showLaunchGateAd':
          setAdType('launch_gate');
          setShowInterstitial(true);
          break;
        case 'showTaskCompletionAd':
          setAdType('task_completion');
          setShowInterstitial(true);
          break;
        case 'showExitTransitionAd':
          setAdType('exit_transition');
          setFromModule(data?.fromModule);
          setShowInterstitial(true);
          break;
      }
    });
    
    return () => unsubscribe();
  }, [isPro]);
  
  // Don't render for Pro users or native apps
  if (isPro || isNativeApp()) return null;
  
  return (
    <InterstitialAdModal 
      isOpen={showInterstitial}
      onClose={() => setShowInterstitial(false)}
      adType={adType}
      fromModule={fromModule}
    />
  );
};

/**
 * Hook for triggering exit/transition ads
 * Use this when navigating away from major modules
 */
export const useExitAd = (moduleName) => {
  const { isPro } = usePro();
  
  const triggerExitAd = useCallback(() => {
    if (isPro) return;
    adLiteService.showExitTransitionAd(moduleName);
  }, [isPro, moduleName]);
  
  return { triggerExitAd };
};

/**
 * Hook for tracking Quran reading time
 */
export const useQuranReadingTracker = () => {
  const { isPro } = usePro();
  
  useEffect(() => {
    if (isPro) return;
    
    // Start tracking when component mounts (user starts reading)
    adLiteService.startQuranReading();
    
    // Check every minute for 10-minute threshold
    const interval = setInterval(() => {
      adLiteService.checkQuranReadingTime();
    }, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [isPro]);
};

/**
 * Function to track forensic scan completion
 */
export const trackForensicScan = () => {
  return adLiteService.trackForensicScan();
};

/**
 * Function to track Personal Du'a generation completion.
 * Call after a successful Personal Du'a generation to trigger a
 * task-completion interstitial for free users (frequency-capped).
 */
export const trackDuaGeneration = () => {
  return adLiteService.trackDuaGeneration();
};

export default AdManager;
